import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { paypalCreateOrder, paypalCaptureOrder, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const CURRENCY = process.env.REACT_APP_PAYPAL_CURRENCY || "EUR";

/**
 * PayPal Smart Button — drops into the Pricing page next to the Stripe CTA.
 * Backend /billing/paypal/create-order and /billing/paypal/capture-order own
 * the transaction state; the client only brokers the PayPal JS SDK calls.
 */
export default function PayPalButton({ packageId = "plus_monthly", onSuccess, disabled }) {
    if (!CLIENT_ID) {
        return (
            <div
                data-testid="paypal-disabled"
                className="text-xs text-[var(--vm-text-muted)] italic text-center py-2"
            >
                PayPal isn't configured — set REACT_APP_PAYPAL_CLIENT_ID to enable.
            </div>
        );
    }

    const createOrder = async () => {
        const { order_id } = await paypalCreateOrder(packageId);
        return order_id;
    };

    const onApprove = async (data) => {
        try {
            const res = await paypalCaptureOrder(data.orderID);
            if (res.applied) {
                toast.success("Payment received — you're Plus! 🌿");
                if (onSuccess) onSuccess(res);
            } else {
                toast.error(`Payment ${res.payment_status} — contact support if charged.`);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const onErr = (err) => {
        // eslint-disable-next-line no-console
        console.error("PayPal error:", err);
        toast.error("PayPal checkout failed. Try again or pay by card.");
    };

    return (
        <div data-testid="paypal-button-wrapper" className={disabled ? "opacity-50 pointer-events-none" : ""}>
            <PayPalScriptProvider
                options={{
                    "client-id": CLIENT_ID,
                    currency: CURRENCY,
                    intent: "capture",
                    components: "buttons",
                    // Surface the standalone "Pay with Debit or Credit Card"
                    // button alongside the blue PayPal button so users
                    // without a PayPal account can still check out as a
                    // guest. PayPal accepts the card and routes the payout
                    // to our PayPal merchant account exactly the same way.
                    "enable-funding": "card,venmo",
                }}
            >
                <PayPalButtons
                    style={{ layout: "vertical", color: "blue", label: "pay", height: 44, tagline: false }}
                    fundingSource={undefined}  // show ALL eligible funding sources (PayPal + Card + …)
                    createOrder={createOrder}
                    onApprove={onApprove}
                    onError={onErr}
                    data-testid="paypal-button"
                />
            </PayPalScriptProvider>
        </div>
    );
}
